/**
 * SavedPosts — bookmarked posts filtered by type, reusing the shared
 * SavedPostsContext (mobile screens/SavedPosts parity).
 */
import { useMemo } from 'react';
import { useProfile } from '@wlb/shared/context/ProfileContext';
import {
  SavedPostsProvider,
  useSavedPosts,
} from '@wlb/shared/context/SavedPostsContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { SAVED_POST_TABS } from '@wlb/shared/constants';
import type { UseUserPostsResult } from '@wlb/shared/hooks/useUserPosts';
import { Tabs } from '../components/ui/Tabs';
import { MorePage } from '../features/more/MorePage';
import { ProfilePostList } from '../features/profile/ProfilePostList';
import '../features/more/more.css';

function SavedPostsContent() {
  const { t } = useTranslation();
  const { profile } = useProfile();
  const saved = useSavedPosts();

  // GetSavedPosts returns the whole list in one call, so the shared post list
  // gets a single non-paginated page.
  const listState = useMemo<UseUserPostsResult>(
    () => ({
      posts: saved.filteredPosts,
      page: 1,
      hasNextPage: false,
      loading: saved.loading,
      isFetchingMore: false,
      error: saved.error ?? '',
      loaded: saved.loaded,
      fetchNextPage: () => undefined,
      refresh: saved.refresh,
      likePost: saved.likePost,
      patchPost: saved.patchPost,
      removePost: saved.deleteLocalPost,
    }),
    [
      saved.deleteLocalPost,
      saved.error,
      saved.filteredPosts,
      saved.likePost,
      saved.loaded,
      saved.loading,
      saved.patchPost,
      saved.refresh,
    ],
  );

  const tabItems = useMemo(
    () =>
      SAVED_POST_TABS.map(key => ({
        key,
        label: t(`savedPosts.tabs.${key.toLowerCase()}`),
      })),
    [t],
  );

  return (
    <MorePage title={t('savedPosts.title')}>
      <Tabs
        items={tabItems}
        activeKey={saved.activeTab}
        onChange={saved.setActiveTab}
        label={t('savedPosts.accessibility.tabs')}
        idPrefix="saved-posts-tab"
      />

      <div
        role="tabpanel"
        id={`saved-posts-tab-panel-${saved.activeTab}`}
        aria-labelledby={`saved-posts-tab-${saved.activeTab}`}
      >
        <ProfilePostList
          posts={listState}
          viewerProfile={profile}
          emptyMessage={t('savedPosts.emptyDescription')}
          // Every row here is saved, so the menu's save entry always unsaves.
          onToggleSave={post => { saved.removePost(post.id).catch(() => undefined); }}
        />
      </div>
    </MorePage>
  );
}

export function SavedPosts() {
  return (
    <SavedPostsProvider>
      <SavedPostsContent />
    </SavedPostsProvider>
  );
}
